
import {
  defineComponent,
  PropType,
  ref,
  computed,
  watch,
  onMounted,
  nextTick,
} from 'vue';
import { useI18n } from 'vue-i18n';
import InputText from 'primevue/inputtext';
import InputNumber from 'primevue/inputnumber';
import Button from 'primevue/button';
import OverlayPanel from 'primevue/overlaypanel';
import CustomColorPicker from '@/components/fields/partials/CustomColorPicker.vue';
import FileManager from '@/components/file-manager/FileManager.vue';
import {
  FieldConfig,
  ParserFieldObject,
  Property,
  File,
} from '@/types';
import {
  replaceTranslationInState,
  addTranslationsToState,
  TemplateEditorState as state,
  syncGameFunnelSteps,
} from '@/composables/template-editor/TemplateEditor';
import {
  createTranslationIdentifier,
  removeTranslationInState,
} from '@/components/template-builder/utils/translate';
import {
  Lang,
  UserState,
  getShopDefaultLang,
} from '@/composables/User';
import { getTemplateIframeDocument } from '@/components/template-builder/utils/helpers';

interface GamePrize {
  label: string;
  probability: number;
  color?: string;
  image?: string;
  translationKey?: string;
  templateId?: number;
  stock?: number | null;
}

export default defineComponent({
  name: 'GamePrizesEditor',

  components: {
    InputText,
    InputNumber,
    Button,
    OverlayPanel,
    CustomColorPicker,
    FileManager,
  },

  props: {
    configs: {
      type: Object as PropType<FieldConfig>,
      required: true,
    },

    parserValues: {
      type: Object as PropType<ParserFieldObject>,
      required: true,
    },
  },

  emits: ['on-change-properties'],

  setup(props, { emit }) {
    const { t } = useI18n();
    const property = ref<Property>(props.parserValues.properties[0]);
    const prizes = ref<GamePrize[]>([]);
    const colorPickerOverlay = ref();
    const activeColorIndex = ref<number | null>(null);
    const displayImageModal = ref(false);
    const activeImageIndex = ref<number | null>(null);

    const showColorPicker = computed(() => props.configs.options?.showColorPicker ?? false);
    let internalChange = false;

    const totalProbability = computed(() => {
      return prizes.value.reduce((sum, prize) => sum + (prize.probability || 0), 0);
    });

    /**
     * Stock limits are edited in the separate "Stock" accordion (GameStockEditor), which writes
     * them straight into the iframe attribute. This editor keeps its own copy of the prizes and
     * rewrites the whole attribute on every change, so before each write the stock of every prize
     * is re-read from the iframe (source of truth). Without this, editing a label after setting a
     * stock silently reset every stock to null (SC-3828).
     */
    const syncStockFromIframe = () => {
      try {
        const template = getTemplateIframeDocument();
        if (!template) return;
        const element = template.querySelector(props.parserValues.selector);
        if (!element) return;
        const iframePrizes = JSON.parse(element.getAttribute('data-prizes') || '[]');
        if (!Array.isArray(iframePrizes)) return;
        prizes.value.forEach((_prize: GamePrize, index: number) => {
          const stock = Number(iframePrizes[index]?.stock);
          prizes.value[index].stock = stock > 0 ? stock : null;
        });
      } catch (e) {
        // Unreadable attribute: keep the local copy
      }
    };

    const parseInitialValue = () => {
      try {
        const value = property.value.value;
        if (value && typeof value === 'string') {
          const parsed = JSON.parse(value);
          if (Array.isArray(parsed)) {
            prizes.value = parsed.map((item: any) => {
              let templateId: number | undefined;
              if (item.templateId) {
                try {
                  // templateId is base64 encoded in data-prizes
                  templateId = Number(atob(item.templateId));
                } catch {
                  // Fallback to direct number if not base64
                  templateId = Number(item.templateId);
                }
                if (Number.isNaN(templateId) || templateId <= 0) {
                  templateId = undefined;
                }
              }
              return {
                label: item.label || '',
                probability: item.probability || 0,
                color: item.color || '#FF6B6B',
                image: item.image || '',
                translationKey: item.translationKey || '',
                ...(templateId ? { templateId } : {}),
                stock: (item.stock != null && Number(item.stock) > 0) ? Number(item.stock) : null,
              };
            });

            // Sync templateId and translationKey from iframe (source of truth)
            try {
              const template = getTemplateIframeDocument();
              if (template) {
                const element = template.querySelector(props.parserValues.selector);
                if (element) {
                  const iframePrizesStr = element.getAttribute('data-prizes');
                  if (iframePrizesStr) {
                    const iframePrizes = JSON.parse(iframePrizesStr);
                    prizes.value.forEach((localPrize: GamePrize, index: number) => {
                      if (iframePrizes[index]?.translationKey && !localPrize.translationKey) {
                        localPrize.translationKey = iframePrizes[index].translationKey;
                      }
                      if (iframePrizes[index]?.templateId && !localPrize.templateId) {
                        try {
                          localPrize.templateId = Number(atob(iframePrizes[index].templateId));
                        } catch (e) {
                          localPrize.templateId = Number(iframePrizes[index].templateId);
                        }
                      }
                    });
                  }
                }
              }
            } catch (e) { /* ignore iframe sync errors */ }

            // Stock limits are owned by the "Stock" accordion: always take them from the iframe
            syncStockFromIframe();

            return;
          }
        }
      } catch (e) {
        console.warn('[GamePrizesEditor] Failed to parse initial value:', e);
      }

      // Default prizes if parsing failed
      prizes.value = [
        { label: '10% de réduction', probability: 30, color: '#FF6B6B' },
        { label: '20% de réduction', probability: 25, color: '#4ECDC4' },
        { label: 'Livraison gratuite', probability: 20, color: '#45B7D1' },
        { label: 'Perdu', probability: 25, color: '#DDA0DD' },
      ];
    };

    const emitChange = () => {
      const jsonValue = JSON.stringify(prizes.value);
      internalChange = true;
      property.value.value = jsonValue;
      emit('on-change-properties', {
        selector: props.parserValues.selector,
        properties: [property.value],
      });
      nextTick(() => { internalChange = false; });
    };

    const updateIframeDataPrizes = () => {
      try {
        const template = getTemplateIframeDocument();
        if (!template) return;
        const element = template.querySelector(props.parserValues.selector);
        if (!element) return;

        // Sync translationKey from iframe state (added by translation system)
        const iframePrizesStr = element.getAttribute('data-prizes');
        if (iframePrizesStr) {
          try {
            const iframePrizes = JSON.parse(iframePrizesStr);
            prizes.value.forEach((localPrize: GamePrize, index: number) => {
              if (iframePrizes[index]?.translationKey && !localPrize.translationKey) {
                localPrize.translationKey = iframePrizes[index].translationKey;
              }
              if (iframePrizes[index]?.templateId && !localPrize.templateId) {
                try {
                  localPrize.templateId = Number(atob(iframePrizes[index].templateId));
                } catch (e) {
                  localPrize.templateId = Number(iframePrizes[index].templateId);
                }
              }
            });
          } catch (e) { /* ignore parse errors */ }
        }

        const prizesForDom = prizes.value.map((p: GamePrize) => ({
          ...p,
          templateId: p.templateId ? btoa(String(p.templateId)) : undefined,
        }));
        element.setAttribute('data-prizes', JSON.stringify(prizesForDom));

        // Refresh wheel via iframe's SPMGames global (handles replaced DOM elements)
        const iframeWindow = (document.querySelector('iframe.template-wrapper') as HTMLIFrameElement)?.contentWindow as any;
        if (iframeWindow?.shopimind?.SPMGames?.refreshWheelByElement) {
          iframeWindow.shopimind.SPMGames.refreshWheelByElement(element);
        } else if ((element as any)._spmGameWheel) {
          (element as any)._spmGameWheel.refresh();
        }
      } catch (e) {
        // Iframe not available
      }
    };

    const onPrizeChange = () => {
      syncStockFromIframe();
      // Sync prize labels to translation state
      const defaultLang = getShopDefaultLang();
      prizes.value.forEach((prize) => {
        if (prize.translationKey) {
          replaceTranslationInState(prize.translationKey, defaultLang, prize.label);
        }
      });
      emitChange();
      // Directly update iframe for immediate visual feedback
      updateIframeDataPrizes();
      // Sync funnel steps to reflect label changes
      syncGameFunnelSteps();
    };

    const onProbabilityChange = (index: number, newValue: number | null) => {
      if (newValue === null) {
        prizes.value[index].probability = 0;
        onPrizeChange();
        return;
      }

      // Calculate total of other prizes (excluding current one)
      const otherTotal = prizes.value.reduce((sum, prize, i) => {
        return i === index ? sum : sum + (prize.probability || 0);
      }, 0);

      // If new value would exceed 100%, cap it
      const maxAllowed = 100 - otherTotal;
      if (newValue > maxAllowed) {
        prizes.value[index].probability = Math.max(0, maxAllowed);
      } else {
        prizes.value[index].probability = newValue;
      }

      onPrizeChange();
    };

    const toggleColorPicker = (event: MouseEvent, index: number) => {
      activeColorIndex.value = index;
      colorPickerOverlay.value.toggle(event);
    };

    const onColorChange = (color: string) => {
      if (activeColorIndex.value !== null && prizes.value[activeColorIndex.value]) {
        syncStockFromIframe();
        prizes.value[activeColorIndex.value].color = color;
        emitChange();
        updateIframeDataPrizes();
      }
    };

    const openImagePicker = (index: number) => {
      activeImageIndex.value = index;
      displayImageModal.value = true;
    };

    const onChooseImage = (image: File) => {
      if (activeImageIndex.value !== null && prizes.value[activeImageIndex.value]) {
        syncStockFromIframe();
        prizes.value[activeImageIndex.value].image = image.url;
        emitChange();
        updateIframeDataPrizes();
      }
      displayImageModal.value = false;
      activeImageIndex.value = null;
    };

    const removeImage = (index: number) => {
      if (prizes.value[index]) {
        syncStockFromIframe();
        prizes.value[index].image = '';
        emitChange();
        updateIframeDataPrizes();
      }
    };

    const addPrize = () => {
      syncStockFromIframe();
      const colors = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#DDA0DD', '#FFEAA7', '#74B9FF', '#FD79A8'];
      const colorIndex = prizes.value.length % colors.length;

      // Create a translation entry for the new prize
      const key = createTranslationIdentifier();
      const normalizedKey = `${key}.data-spm-text`;
      const defaultLabel = 'Nouveau prix';

      const translations = UserState.activeShop?.langs.map((language: Lang) => ({
        section: state.template?.activeSection ?? 0,
        language: language.id,
        fieldType: 'data-spm-text',
        key: normalizedKey,
        value: defaultLabel,
        callback: (value: string) => value,
      })) ?? [];

      addTranslationsToState(translations);

      prizes.value.push({
        label: defaultLabel,
        probability: 0,
        color: colors[colorIndex],
        translationKey: normalizedKey,
      });
      emitChange();
      updateIframeDataPrizes();
      syncGameFunnelSteps();
    };

    const removePrize = (index: number) => {
      if (prizes.value.length > 2) {
        // Read the stocks before the splice so each stock follows its prize, not its position
        syncStockFromIframe();
        const prize = prizes.value[index];
        // Remove translation entries for this prize
        if (prize.translationKey) {
          removeTranslationInState(prize.translationKey);
        }
        prizes.value.splice(index, 1);
        emitChange();
        updateIframeDataPrizes();
        syncGameFunnelSteps();
      }
    };

    onMounted(() => {
      parseInitialValue();
      // Sync funnel steps on initial load
      nextTick(() => syncGameFunnelSteps());
    });

    watch(() => props.parserValues.properties[0]?.value, () => {
      if (!internalChange) {
        parseInitialValue();
        nextTick(() => syncGameFunnelSteps());
      }
    }, { deep: true });

    return {
      t,
      prizes,
      showColorPicker,
      totalProbability,
      colorPickerOverlay,
      activeColorIndex,
      displayImageModal,
      onPrizeChange,
      onProbabilityChange,
      toggleColorPicker,
      onColorChange,
      openImagePicker,
      onChooseImage,
      removeImage,
      addPrize,
      removePrize,
    };
  },
});
